import { useRef } from "react";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";

export const getDeviceId = async (videoInputDevices, facingMode) => {
  videoInputDevices = videoInputDevices.filter((deviceInfo) => deviceInfo.kind === "videoinput");

  if (videoInputDevices.length < 1) {
    throw new Error("No video input devices found");
  }

  const regex = facingMode === "environment" ? /rear|back|environment/gi : /front|user|face/gi;

  const devices = videoInputDevices.filter((videoDevice) => regex.test(videoDevice.label));

  if (!devices.length) {
    throw new Error("No video input devices found");
  }

  return devices[0].deviceId;
};

export const styles = {
  container: {
    width: '100%',
    paddingTop: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  video: {
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'block',
    overflow: 'hidden',
    position: 'absolute',
    transform: undefined
  },
};

/**
 * Formate, die der Scanner versuchen soll.
 *
 * Maßgeblich ist, was eine EFN (15 Ziffern) oder eine VNR (19 Ziffern) überhaupt
 * tragen kann. Übrig bleiben QR-Code und Data Matrix für die von dieser App
 * erzeugten Codes sowie Code 39 und Code 128 für die alten EFN-Etiketten mit
 * Strichcode. Beide Strichcode-Formate bleiben bewusst dauerhaft stehen: die
 * Landesärztekammern haben über die Jahre Etiketten in verschiedenen Formaten
 * ausgegeben. Ein Format zu wenig hieße, dass das Etikett gar nicht gelesen wird
 * und der Dialog stumm bleibt; der zweite Decoder kostet dagegen kaum etwas.
 *
 * Bewusst draußen:
 * - ITF: kein Prüfzeichen, keine echte Start-/Stopp-Marke, Standardlänge ITF-14
 *   passt auf fast jedes Streifenmuster. War die Hauptquelle der Phantomtreffer
 *   im ruhenden Dialog. Kodiert ohnehin nur gerade Stellenzahlen und kann eine
 *   EFN oder VNR damit gar nicht tragen.
 * - EAN-8/13, UPC-A/E, RSS-14: 8, 12, 13 bzw. 14 Ziffern, können eine EFN oder
 *   VNR ebenfalls nie tragen. Sie zu lesen bringt nur einen Ausgang - "keine EFN
 *   erkannt" - kostet aber genau das, was gemeldet wurde: Produktcodes, die
 *   zufällig im Raum liegen (Verpackung auf dem Tisch, Etikett am Netzteil),
 *   werden mitgelesen und melden sich im ruhenden Dialog von selbst.
 * - AZTEC, PDF_417: kommen weder auf EFN-Etiketten noch auf den QR-Codes dieser
 *   App vor.
 *
 * Ohne diese Liste (hints === null) baut ZXing sich intern einen eigenen Satz
 * zusammen - mit genau den ausgeschlossenen Formaten und den 1D-Decodern vorn.
 * Damit die Beschränkung ankommt, muss sie als Hints-Map rein: createScanHints().
 */
export const SCAN_FORMATS = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_128,
];

/**
 * ZXing erwartet die Formatliste als Map-Eintrag POSSIBLE_FORMATS. Ein Objekt
 * { possibleFormats: [...] } an den Konstruktor zu reichen wirkt nicht:
 * BrowserMultiFormatReader nimmt nur (hints, timeBetweenScansMillis) entgegen,
 * alles Weitere fällt kommentarlos unter den Tisch.
 */
export const createScanHints = (formats = SCAN_FORMATS) => {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
  return hints;
};

const LINEAR_FORMATS = new Set([
  BarcodeFormat.CODABAR,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODE_128,
  BarcodeFormat.EAN_8,
  BarcodeFormat.EAN_13,
  BarcodeFormat.ITF,
  BarcodeFormat.RSS_14,
  BarcodeFormat.RSS_EXPANDED,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.UPC_EAN_EXTENSION,
]);

/**
 * Strichcode (1D) statt Flächencode (2D)?
 *
 * Nur die 1D-Decoder brauchen die Wiederholungsprüfung unten. QR-Code und Data
 * Matrix bringen Fehlerkorrektur und Formatprüfung mit; ein Treffer ist dort
 * praktisch immer ein echter Code und darf sofort gemeldet werden - auch wenn
 * er nur kurz vor die Kamera gehalten wurde.
 *
 * Achtung beim Ändern: BarcodeFormat.AZTEC ist 0 und damit falsy - deshalb hier
 * explizit gegen undefined/null prüfen statt auf Wahrheitswert.
 */
export const isLinearFormat = (format) =>
  format !== undefined && format !== null && LINEAR_FORMATS.has(format);

/**
 * Trennt echte Fehlscans von Phantomtreffern der 1D-Decoder.
 *
 * Ausgangslage: Code 39, Code 128 und ITF laufen ohne Prüfziffer und tasten nur
 * Hell-Dunkel-Folgen ab. Auf beliebigem Bildinhalt - Kleidung, Regal, Rauschen
 * bei wenig Licht - liefern sie deshalb immer wieder Treffer, obwohl gar kein
 * Code vor der Kamera liegt. Bei rund zwei Scans pro Sekunde schlägt das nach
 * einigen Minuten Stillstand zuverlässig zu; genau daher kamen die
 * "nicht erkannt"-Toasts im ruhenden Dialog.
 *
 * Der Unterschied zum echten Fehlscan liegt in der Wiederholung: ein Code, den
 * jemand vor die Kamera hält, wird Sekunde für Sekunde mit identischem Inhalt
 * gelesen. Ein Phantomtreffer entsteht aus dem Zufallsmuster eines einzelnen
 * Bildes und wiederholt sich praktisch nie.
 *
 * Also: erst melden, wenn derselbe Inhalt innerhalb des Zeitfensters ein
 * zweites Mal ankommt. Ein falsch hingehaltener Barcode kostet das eine halbe
 * Sekunde Verzögerung, ein Phantomtreffer kommt gar nicht erst durch.
 *
 * @returns Prüffunktion (text) => boolean - true, wenn der Inhalt bestätigt ist
 */
export const useWiederholungsBestaetigung = (maxAbstandMs = 1500) => {
  const letzterTreffer = useRef({ text: null, zeit: 0 });

  return (text) => {
    const jetzt = Date.now();
    const bestaetigt =
      letzterTreffer.current.text === text &&
      jetzt - letzterTreffer.current.zeit <= maxAbstandMs;
    letzterTreffer.current = { text, zeit: jetzt };
    return bestaetigt;
  };
};

const FOKUS = {
  continuous: "Autofokus laufend",
  "single-shot": "Autofokus einmalig",
  manual: "Fokus manuell",
  none: "Fixfokus",
};

const fokusText = (modus) => FOKUS[modus] || modus;

/**
 * Diagnosezeile unter dem Sucher: welche Kamera, mit welcher Auflösung und
 * welchem Fokus gerade arbeitet.
 *
 * Anlass: Auf manchen Telefonen findet die Kamera keinen Fokus, auf anderen ist
 * dieselbe Teilnehmerliste sofort scharf. Zwei Dinge legt dabei die App fest,
 * nicht das Gerät: welche Rückkamera benutzt wird (getDeviceId nimmt die erste,
 * deren Name passt - bei Telefonen mit mehreren Linsen nicht zwingend die
 * Hauptkamera, und ein Ultraweitwinkel hat oft Fixfokus) und welche Auflösung
 * angefordert wird (gar keine - der Browser nimmt seine Vorgabe). Statt das aus
 * der Ferne zu raten, steht es hier, und ein Veranstalter kann es am Telefon
 * vorlesen.
 *
 * "möglich bis" erscheint nur, wenn die Kamera mehr kann, als sie liefert: das
 * ist genau der Befund, der für eine angeforderte Auflösung spräche.
 * getCapabilities fehlt in manchen Browsern ganz; dann bleibt es bei dem, was
 * getSettings sagt.
 *
 * @param {MediaStreamTrack} track Videospur des laufenden Scanners
 * @returns {string|null} z. B. "camera2 0, facing back · 640 × 480 (möglich bis 4000 × 3000) · Autofokus laufend"
 */
export const beschreibeKamera = (track) => {
  if (!track) {
    return null;
  }
  const settings = typeof track.getSettings === "function" ? track.getSettings() || {} : {};
  let caps = {};
  try {
    caps = typeof track.getCapabilities === "function" ? track.getCapabilities() || {} : {};
  } catch (e) {
    caps = {};
  }

  const teile = [track.label || "Kamera ohne Namen"];

  if (settings.width && settings.height) {
    const maxB = caps.width?.max;
    const maxH = caps.height?.max;
    const mehr = maxB && maxH && maxB * maxH > settings.width * settings.height;
    teile.push(`${settings.width} × ${settings.height}` + (mehr ? ` (möglich bis ${maxB} × ${maxH})` : ""));
  }

  const moeglich = Array.isArray(caps.focusMode) ? caps.focusMode : [];
  if (settings.focusMode) {
    const andere = moeglich.filter((m) => m !== settings.focusMode);
    teile.push(fokusText(settings.focusMode) + (andere.length ? ` (möglich: ${andere.map(fokusText).join(", ")})` : ""));
  } else if (moeglich.length) {
    teile.push(`Fokus möglich: ${moeglich.map(fokusText).join(", ")}`);
  } else {
    teile.push("Fokus: keine Angabe");
  }

  return teile.join(" · ");
};
